import React, { useMemo } from 'react';
import { Loan } from '../types/loan';
import { ChevronRightIcon, UsersIcon } from 'lucide-react';
interface ManagerDashboardProps {
  loans: Loan[];
  currentMarketRate: number;
  onSelectLO: (loName: string) => void;
  managerName?: string | null;
}
export function ManagerDashboard({
  loans,
  currentMarketRate,
  onSelectLO,
  managerName
}: ManagerDashboardProps) {
  const loStats = useMemo(() => {
    const stats: Record<string, {
      count: number;
      volume: number;
      rateDiffs: number[];
    }> = {};
    loans.forEach(loan => {
      if (!stats[loan.loanOfficer]) {
        stats[loan.loanOfficer] = {
          count: 0,
          volume: 0,
          rateDiffs: []
        };
      }
      stats[loan.loanOfficer].count += 1;
      stats[loan.loanOfficer].volume += loan.loanAmount;
      stats[loan.loanOfficer].rateDiffs.push(loan.interestRate - currentMarketRate);
    });
    return Object.entries(stats).map(([name, data]) => ({
      name,
      count: data.count,
      volume: data.volume,
      avgRateDiff: data.count > 0 ? data.rateDiffs.reduce((a, b) => a + b, 0) / data.count : 0,
      maxRateDiff: data.count > 0 ? Math.max(...data.rateDiffs) : 0
    })).sort((a, b) => b.volume - a.volume);
  }, [loans, currentMarketRate]);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-229">
      <div className="px-6 py-5 border-b border-gray-200 flex items-center gap-2" data-id="element-230">
        <UsersIcon className="w-5 h-5 text-gray-500" data-id="element-231" />
        <h3 className="text-lg font-semibold text-gray-800" data-id="element-232">
          {managerName ? `${managerName}'s Team` : 'All Loan Officers'}
        </h3>
      </div>
      <div className="overflow-x-auto" data-id="element-233">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-234">
          <thead className="bg-gray-50/80" data-id="element-235">
            <tr data-id="element-236">
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-237">
                Loan Officer
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-238">
                Matching Loans
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-239">
                Total Volume
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-240">
                Avg Rate Diff
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-241">
                Highest Rate Diff
              </th>
              <th className="px-6 py-3.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-242">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-243">
            {loStats.map(stat => <tr key={stat.name} className="hover:bg-gray-50 transition-colors cursor-pointer group" onClick={() => onSelectLO(stat.name)} data-id="element-244">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-245">
                  <div className="font-medium text-gray-900" data-id="element-246">{stat.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-247">
                  {stat.count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-248">
                  {formatCurrency(stat.volume)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-249">
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset text-cyan-700 bg-cyan-50 ring-cyan-600/20" data-id="element-250">
                    +{stat.avgRateDiff.toFixed(2)}%
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-251">
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset text-red-700 bg-red-50 ring-red-600/20" data-id="element-252">
                    +{stat.maxRateDiff.toFixed(2)}%
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-253">
                  <button className="inline-flex items-center text-violet-600 group-hover:text-violet-900 transition-colors" data-id="element-254">
                    View List <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-255" />
                  </button>
                </td>
              </tr>)}
            {loStats.length === 0 && <tr data-id="element-256">
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500" data-id="element-257">
                  No data available for the selected filters.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
}